import type { Communication } from '@medplum/fhirtypes'
import {
  getParticipantLastDeliveredAt,
  getParticipantLastReadAt,
  type PractitionerThreadOption,
} from './service'

/**
 * Delivery state of a message the current practitioner sent.
 *
 * `sending` and `failed` are client-local and deliberately not persisted — a
 * message that never reached the server does not exist after a reload, which is
 * how every mainstream messenger behaves. `sent` and `read` are derived from
 * server state, so they survive reloads, devices, and sessions.
 *
 * `delivered` means the recipient's client has fetched the message, recorded by
 * POST /v2/threads/acknowledge-delivery (MESSAGES_V2_DESIGN.md §5).
 */
export type ReceiptStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed'

export const RECEIPT_LABELS: Record<ReceiptStatus, string> = {
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Not sent',
}

/**
 * Resolve an outgoing message to `read`, `delivered`, or `sent`.
 *
 * Threads are 1:1 today, but requiring *all* other participants keeps the rule
 * correct if group threads ever land: a message is Read only once everyone has
 * read it, and Delivered only once it has reached everyone.
 */
export function resolveReceiptStatus(
  message: Communication,
  thread: Communication,
  otherParticipants: PractitionerThreadOption[],
): ReceiptStatus {
  const sentAt = message.sent
  if (!sentAt || otherParticipants.length === 0) {
    return 'sent'
  }

  // Both pointers hold the `sent` of the newest message that participant had
  // seen, so an equal timestamp means this very message is the one covered.
  const coveredByEveryone = (
    readPointer: (thread: Communication, practitionerId: string) => string | undefined,
  ): boolean =>
    otherParticipants.every((participant) => {
      const practitionerId = participant.practitioner.id
      if (!practitionerId) {
        return false
      }

      const pointer = readPointer(thread, practitionerId)
      return Boolean(pointer && pointer >= sentAt)
    })

  if (coveredByEveryone(getParticipantLastReadAt)) {
    return 'read'
  }

  return coveredByEveryone(getParticipantLastDeliveredAt) ? 'delivered' : 'sent'
}
